import React, { useState } from 'react';
import {
  Search, Mail, Phone, MapPin,
  Eye, Edit, Trash2, UserPlus, UserCheck, UserX,
  XCircle, Users, Save, ChevronLeft, ChevronRight,
} from 'lucide-react';
import { toast } from 'react-toastify';
import Layout from '../components/Layout';
import {
  useGetCustomersQuery,
  useGetCustomerQuery,
  useCreateCustomerMutation,
  useUpdateCustomerMutation,
  useUpdateCustomerStatusMutation,
  useDeleteCustomerMutation,
} from '../app/api';

const Customers = () => {
  // ============================================
  // STATE
  // ============================================
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('All');
  const [filterType, setFilterType] = useState('All');
  const [sortBy, setSortBy] = useState('newest');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    type: 'Regular',
    status: 'Active',
    notes: '',
  });
  const [errors, setErrors] = useState({});

  // ============================================
  // ✅ RTK QUERY — Automatic caching
  // ============================================
  const {
    data: customersData,
    isLoading,
    isFetching,
  } = useGetCustomersQuery({
    page,
    limit,
    search: searchTerm || undefined,
    status: filterStatus !== 'All' ? filterStatus : undefined,
    type: filterType !== 'All' ? filterType : undefined,
    sortBy,
  });

  // ✅ Single customer for view modal (auto cached)
  const { data: viewData } = useGetCustomerQuery(selectedId, {
    skip: !selectedId || !showViewModal,
  });

  // ✅ Mutations
  const [createCustomer, { isLoading: creating }] = useCreateCustomerMutation();
  const [updateCustomer, { isLoading: updating }] = useUpdateCustomerMutation();
  const [updateStatus] = useUpdateCustomerStatusMutation();
  const [deleteCustomer] = useDeleteCustomerMutation();

  // ============================================
  // DATA
  // ============================================
  const customers = customersData?.data?.customers || [];
  const stats = customersData?.data?.stats || {
    total: 0,
    active: 0,
    inactive: 0,
    blocked: 0,
  };
  const pagination = customersData?.data?.pagination || {
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 0,
    hasNextPage: false,
    hasPrevPage: false,
  };

  // ============================================
  // HELPERS
  // ============================================
  const formatCurrency = (amount) => '₹' + (amount || 0).toLocaleString();

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const getTypeBadge = (type) => {
    const classes = {
      Premium: 'badge-primary',
      VIP: 'badge-success',
      Regular: 'badge-info',
    };
    return classes[type] || 'badge-info';
  };

  // ============================================
  // FORM HANDLERS
  // ============================================
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const validate = () => {
    const e = {};
    if (!formData.name.trim()) e.name = 'Name is required';
    if (!formData.email.trim()) e.email = 'Email is required';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      e.email = 'Invalid email format';
    }
    if (!formData.phone.trim()) e.phone = 'Phone is required';
    return e;
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      address: '',
      type: 'Regular',
      status: 'Active',
      notes: '',
    });
    setErrors({});
  };

  // ============================================
  // CREATE
  // ============================================
  const handleCreate = async (e) => {
    e.preventDefault();
    const e2 = validate();
    if (Object.keys(e2).length > 0) {
      setErrors(e2);
      toast.error('Please fix the errors');
      return;
    }

    try {
      await createCustomer(formData).unwrap();
      toast.success('Customer created successfully!');
      setShowAddModal(false);
      resetForm();
      // ✅ Auto refetch due to invalidatesTags
    } catch (error) {
      toast.error(error?.data?.message || 'Failed to create customer');
    }
  };

  // ============================================
  // EDIT
  // ============================================
  const handleEditClick = (customer) => {
    setSelectedCustomer(customer);
    setFormData({
      name: customer.name || '',
      email: customer.email || '',
      phone: customer.phone || '',
      address: customer.addresses?.[0]?.street || '',
      type: customer.customerType || 'Regular',
      status: customer.status || 'Active',
      notes: customer.notes || '',
    });
    setShowEditModal(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    const e2 = validate();
    if (Object.keys(e2).length > 0) {
      setErrors(e2);
      toast.error('Please fix the errors');
      return;
    }

    try {
      await updateCustomer({
        id: selectedCustomer._id,
        data: formData,
      }).unwrap();
      toast.success('Customer updated successfully!');
      setShowEditModal(false);
      resetForm();
      // ✅ Auto refetch
    } catch (error) {
      toast.error(error?.data?.message || 'Failed to update customer');
    }
  };

  // ============================================
  // STATUS UPDATE
  // ============================================
  const handleStatusChange = async (id, newStatus) => {
    setUpdatingId(id);
    try {
      await updateStatus({ id, status: newStatus }).unwrap();
      toast.success(`Status changed to ${newStatus}`);
      // ✅ Auto refetch
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setUpdatingId(null);
    }
  };

  // ============================================
  // DELETE
  // ============================================
  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this customer?')) return;

    try {
      await deleteCustomer(id).unwrap();
      toast.success('Customer deleted successfully');

      // ✅ Agar last item tha toh previous page pe jao
      if (customers.length === 1 && page > 1) {
        setPage(page - 1);
      }
      // Auto refetch ho jayega
    } catch (error) {
      toast.error(error?.data?.message || 'Failed to delete customer');
    }
  };

  // ============================================
  // VIEW
  // ============================================
  const handleView = (customer) => {
    setSelectedId(customer._id);
    setShowViewModal(true);
    // ✅ RTK Query auto fetch karega (agar cache mein nahi hai)
  };

  // ============================================
  // PAGINATION
  // ============================================
  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > pagination.totalPages) return;
    setPage(newPage);
  };

  const handleLimitChange = (e) => {
    setLimit(parseInt(e.target.value));
    setPage(1);
  };

  const getPageNumbers = () => {
    const { page: currentPage, totalPages } = pagination;
    const pages = [];
    const maxVisible = 5;

    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);

    if (end - start < maxVisible - 1) {
      start = Math.max(1, end - maxVisible + 1);
    }

    if (start > 1) {
      pages.push(1);
      if (start > 2) pages.push('...');
    }

    for (let i = start; i <= end; i++) pages.push(i);

    if (end < totalPages) {
      if (end < totalPages - 1) pages.push('...');
      pages.push(totalPages);
    }

    return pages;
  };

  // ============================================
  // RENDER
  // ============================================
  return (
    <Layout>
      <div className="customers-page">
        {/* Header */}
        <div className="page-header">
          <div>
            <h1 className="page-title">Customers</h1>
            <p className="page-subtitle">Manage your customer base and relationships</p>
          </div>
          <div className="page-actions">
            <button
              className="btn btn-primary"
              onClick={() => {
                resetForm();
                setShowAddModal(true);
              }}
            >
              <UserPlus size={18} />
              Add Customer
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="customers-stats">
          <div className="stat-item">
            <div className="stat-icon" style={{ background: 'var(--color-primary)' }}>
              <Users size={20} />
            </div>
            <div>
              <span className="stat-label">Total</span>
              <span className="stat-value">{stats.total}</span>
            </div>
          </div>
          <div className="stat-item">
            <div className="stat-icon" style={{ background: 'var(--color-success)' }}>
              <UserCheck size={20} />
            </div>
            <div>
              <span className="stat-label">Active</span>
              <span className="stat-value">{stats.active}</span>
            </div>
          </div>
          <div className="stat-item">
            <div className="stat-icon" style={{ background: 'var(--color-warning)' }}>
              <UserX size={20} />
            </div>
            <div>
              <span className="stat-label">Inactive</span>
              <span className="stat-value">{stats.inactive}</span>
            </div>
          </div>
          <div className="stat-item">
            <div className="stat-icon" style={{ background: 'var(--color-danger)' }}>
              <XCircle size={20} />
            </div>
            <div>
              <span className="stat-label">Blocked</span>
              <span className="stat-value">{stats.blocked}</span>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="customers-filters card">
          <div className="filter-row">
            <div className="filter-group">
              <div className="search-box">
                <Search size={18} className="search-icon" />
                <input
                  type="text"
                  placeholder="Search customers..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setPage(1);
                  }}
                  className="search-input"
                />
                {searchTerm && (
                  <button className="clear-search" onClick={() => setSearchTerm('')}>
                    ✕
                  </button>
                )}
              </div>
            </div>

            <div className="filter-group">
              <select
                className="filter-select"
                value={filterStatus}
                onChange={(e) => {
                  setFilterStatus(e.target.value);
                  setPage(1);
                }}
              >
                <option value="All">All Status</option>
                <option value="Active">Active ({stats.active})</option>
                <option value="Inactive">Inactive ({stats.inactive})</option>
                <option value="Blocked">Blocked ({stats.blocked})</option>
              </select>

              <select
                className="filter-select"
                value={filterType}
                onChange={(e) => {
                  setFilterType(e.target.value);
                  setPage(1);
                }}
              >
                <option value="All">All Types</option>
                <option value="Regular">Regular</option>
                <option value="VIP">VIP</option>
                <option value="Premium">Premium</option>
              </select>

              <select
                className="filter-select"
                value={sortBy}
                onChange={(e) => {
                  setSortBy(e.target.value);
                  setPage(1);
                }}
              >
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
                <option value="spent-high">Highest Spent</option>
                <option value="spent-low">Lowest Spent</option>
                <option value="orders-high">Most Orders</option>
                <option value="orders-low">Least Orders</option>
              </select>

              <select
                className="filter-select"
                value={limit}
                onChange={handleLimitChange}
              >
                <option value="10">10 / page</option>
                <option value="25">25 / page</option>
                <option value="50">50 / page</option>
                <option value="100">100 / page</option>
              </select>
            </div>
          </div>
        </div>

        {/* Content */}
        {isLoading ? (
          <div className="flex-center" style={{ height: '300px' }}>
            <div className="spinner" />
          </div>
        ) : customers.length === 0 ? (
          <div className="card">
            <div className="empty-state">
              <div className="empty-state-content">
                <span className="empty-icon">👥</span>
                <h3>No customers found</h3>
                <p>Try adjusting your search or filter criteria</p>
              </div>
            </div>
          </div>
        ) : (
          <div className="card customers-table-container">
            <div className="table-container">
              <table className="table customers-table">
                <thead>
                  <tr>
                    <th>Customer</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Joined</th>
                    <th>Orders</th>
                    <th>Spent</th>
                    <th>Points</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th style={{ width: '120px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {customers.map((customer) => (
                    <tr key={customer._id} className="customer-row">
                      <td>
                        <div className="customer-info">
                          <div className="customer-avatar">
                            {customer.avatar || customer.name?.charAt(0).toUpperCase()}
                          </div>
                          <span className="customer-name">{customer.name}</span>
                        </div>
                      </td>
                      <td>
                        <span className="customer-email">{customer.email}</span>
                      </td>
                      <td>
                        <span className="customer-phone">{customer.phone}</span>
                      </td>
                      <td>
                        <span className="customer-join">
                          {formatDate(customer.createdAt)}
                        </span>
                      </td>
                      <td>
                        <span className="customer-orders-count">
                          {customer.totalOrders || 0}
                        </span>
                      </td>
                      <td>
                        <span className="customer-spent">
                          {formatCurrency(customer.totalSpent)}
                        </span>
                      </td>
                      <td>
                        <span className="customer-points">
                          {customer.loyaltyPoints || 0}
                        </span>
                      </td>
                      <td>
                        <span className={`badge ${getTypeBadge(customer.customerType)}`}>
                          {customer.customerType || 'Regular'}
                        </span>
                      </td>
                      <td>
                        <div className="status-select-wrapper">
                          <select
                            className={`status-select ${(customer.status || '').toLowerCase()}`}
                            value={customer.status}
                            onChange={(e) => handleStatusChange(customer._id, e.target.value)}
                            disabled={updatingId === customer._id}
                          >
                            <option value="Active">Active</option>
                            <option value="Inactive">Inactive</option>
                            <option value="Blocked">Blocked</option>
                          </select>
                        </div>
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button
                            className="btn btn-sm btn-outline"
                            onClick={() => handleView(customer)}
                            title="View"
                          >
                            <Eye size={16} />
                          </button>
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={() => handleEditClick(customer)}
                            title="Edit"
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDelete(customer._id)}
                            title="Delete"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.total > 0 && (
              <div className="table-footer">
                <div className="table-info">
                  Showing{' '}
                  <strong>{(pagination.page - 1) * pagination.limit + 1}</strong> to{' '}
                  <strong>
                    {Math.min(pagination.page * pagination.limit, pagination.total)}
                  </strong>{' '}
                  of <strong>{pagination.total}</strong> customers
                </div>

                <div className="pagination">
                  <button
                    className="btn btn-sm btn-outline"
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={!pagination.hasPrevPage}
                  >
                    <ChevronLeft size={16} />
                    Prev
                  </button>

                  {getPageNumbers().map((p, i) =>
                    p === '...' ? (
                      <span key={`dots-${i}`} className="pagination-dots">
                        ...
                      </span>
                    ) : (
                      <button
                        key={p}
                        className={`btn btn-sm ${
                          pagination.page === p ? 'btn-primary' : 'btn-outline'
                        }`}
                        onClick={() => handlePageChange(p)}
                      >
                        {p}
                      </button>
                    )
                  )}

                  <button
                    className="btn btn-sm btn-outline"
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={!pagination.hasNextPage}
                  >
                    Next
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ============================================ */}
      {/* VIEW MODAL */}
      {/* ============================================ */}
      {showViewModal && viewData?.data && (
        <div className="modal-overlay" onClick={() => setShowViewModal(false)}>
          <div className="modal customer-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-user-info">
                <div className="modal-avatar-large">
                  {viewData.data.avatar || viewData.data.name?.charAt(0).toUpperCase()}
                </div>
                <div>
                  <h2 className="modal-title">{viewData.data.name}</h2>
                  <p className="modal-subtitle">
                    Customer since {formatDate(viewData.data.createdAt)}
                  </p>
                </div>
              </div>
              <button className="modal-close" onClick={() => setShowViewModal(false)}>
                ✕
              </button>
            </div>

            <div className="customer-modal-content">
              <div className="customer-modal-stats">
                <div className="stat-item">
                  <span className="stat-label">Total Orders</span>
                  <span className="stat-value">{viewData.data.totalOrders || 0}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Total Spent</span>
                  <span className="stat-value">{formatCurrency(viewData.data.totalSpent)}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Loyalty Points</span>
                  <span className="stat-value">{viewData.data.loyaltyPoints || 0}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Customer Type</span>
                  <span className="stat-value">
                    <span className={`badge ${getTypeBadge(viewData.data.customerType)}`}>
                      {viewData.data.customerType || 'Regular'}
                    </span>
                  </span>
                </div>
              </div>

              <div className="customer-details-grid">
                <div className="detail-section">
                  <h4>Contact Information</h4>
                  <div className="detail-item">
                    <Mail size={16} />
                    <span>{viewData.data.email}</span>
                  </div>
                  <div className="detail-item">
                    <Phone size={16} />
                    <span>{viewData.data.phone}</span>
                  </div>
                </div>

                {viewData.data.addresses?.length > 0 && (
                  <div className="detail-section">
                    <h4>Address</h4>
                    {viewData.data.addresses.map((addr, i) => (
                      <div key={i} className="detail-item">
                        <MapPin size={16} />
                        <span>
                          {addr.street}
                          {addr.city && `, ${addr.city}`}
                          {addr.state && `, ${addr.state}`}
                          {addr.pincode && ` - ${addr.pincode}`}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {viewData.data.orders?.length > 0 && (
                <div className="customer-orders">
                  <h4>Recent Orders</h4>
                  {viewData.data.orders.slice(0, 5).map((order, i) => (
                    <div key={i} className="order-item">
                      <span className="order-id">{order.orderId || order._id}</span>
                      <span className="order-amount">{formatCurrency(order.total)}</span>
                      <span className="badge badge-info">{order.status}</span>
                    </div>
                  ))}
                </div>
              )}

              <div className="modal-actions">
                <button className="btn btn-outline" onClick={() => setShowViewModal(false)}>
                  Close
                </button>
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    setShowViewModal(false);
                    handleEditClick(viewData.data);
                  }}
                >
                  <Edit size={16} />
                  Edit
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* ADD MODAL */}
      {/* ============================================ */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Add New Customer</h2>
              <button className="modal-close" onClick={() => setShowAddModal(false)}>
                ✕
              </button>
            </div>

            <form onSubmit={handleCreate} className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">
                    Name <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={`form-control ${errors.name ? 'error' : ''}`}
                    placeholder="Enter full name"
                  />
                  {errors.name && <div className="form-error">{errors.name}</div>}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    Email <span className="required">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`form-control ${errors.email ? 'error' : ''}`}
                    placeholder="Enter email"
                  />
                  {errors.email && <div className="form-error">{errors.email}</div>}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">
                    Phone <span className="required">*</span>
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className={`form-control ${errors.phone ? 'error' : ''}`}
                    placeholder="Enter phone"
                  />
                  {errors.phone && <div className="form-error">{errors.phone}</div>}
                </div>
                <div className="form-group">
                  <label className="form-label">Customer Type</label>
                  <select
                    name="type"
                    value={formData.type}
                    onChange={handleChange}
                    className="form-control"
                  >
                    <option value="Regular">Regular</option>
                    <option value="VIP">VIP</option>
                    <option value="Premium">Premium</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Status</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="form-control"
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                  <option value="Blocked">Blocked</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Address</label>
                <textarea
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  className="form-control"
                  rows="2"
                  placeholder="Enter address"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Notes</label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  className="form-control"
                  rows="2"
                  placeholder="Internal notes"
                />
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={() => setShowAddModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={creating}>
                  <UserPlus size={18} />
                  {creating ? 'Creating...' : 'Add Customer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* EDIT MODAL */}
      {/* ============================================ */}
      {showEditModal && selectedCustomer && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Edit Customer</h2>
              <button className="modal-close" onClick={() => setShowEditModal(false)}>
                ✕
              </button>
            </div>

            <form onSubmit={handleUpdate} className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">
                    Name <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={`form-control ${errors.name ? 'error' : ''}`}
                  />
                  {errors.name && <div className="form-error">{errors.name}</div>}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    Email <span className="required">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`form-control ${errors.email ? 'error' : ''}`}
                  />
                  {errors.email && <div className="form-error">{errors.email}</div>}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">
                    Phone <span className="required">*</span>
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className={`form-control ${errors.phone ? 'error' : ''}`}
                  />
                  {errors.phone && <div className="form-error">{errors.phone}</div>}
                </div>
                <div className="form-group">
                  <label className="form-label">Customer Type</label>
                  <select
                    name="type"
                    value={formData.type}
                    onChange={handleChange}
                    className="form-control"
                  >
                    <option value="Regular">Regular</option>
                    <option value="VIP">VIP</option>
                    <option value="Premium">Premium</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Status</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="form-control"
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                  <option value="Blocked">Blocked</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Address</label>
                <textarea
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  className="form-control"
                  rows="2"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Notes</label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  className="form-control"
                  rows="2"
                />
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={() => setShowEditModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={updating}>
                  <Save size={18} />
                  {updating ? 'Saving...' : 'Update Customer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Customers;